/**
 * Column model for the Trace (spans) table.
 *
 * Three groups, mirroring runColumns.js:
 *   - FIXED LEADING  : Trace ID column (renders status dot + kind tag + span_id)
 *                      and Name (span.name)
 *   - CONFIGURABLE   : user-toggleable middle columns
 *   - FIXED TRAILING : Time (started_at)
 *
 * Fixed columns cannot be toggled off. Only the configurable middle keys are
 * persisted; fixed columns are re-added by `resolveSpanColumns`.
 */

export const ALL_SPAN_COLUMNS = [
  // ── Fixed leading ──
  // Trace ID cell renders the status dot (red/green) and the kind chip (LLM,
  // Tool, Retrieval, Agent…) inline before the span_id text.
  { key: 'span_id',         label: 'Trace ID',      default: true,  fixed: true },
  { key: 'name',            label: 'Name',          default: true,  fixed: true },

  // ── Configurable middle (ON by default) ──
  { key: 'duration_ms',     label: 'Duration',      default: true },
  { key: 'distinct_id',     label: 'User',          default: true },
  { key: 'run_id',          label: 'Parent run ID', default: true },

  // ── Configurable middle (OFF by default) ──
  { key: 'error',           label: 'Error',         default: false },
  { key: 'agent_name',      label: 'Agent',         default: false },
  { key: 'tool_name',       label: 'Tool',          default: false },
  { key: 'model',           label: 'Model',         default: false },
  { key: 'provider',        label: 'Provider',      default: false },
  { key: 'input_tokens',    label: 'Tokens in',     default: false },
  { key: 'output_tokens',   label: 'Tokens out',    default: false },
  { key: 'cost',            label: 'Cost',          default: false },
  { key: 'temperature',     label: 'Temperature',   default: false },
  { key: 'conversation_id', label: 'Conversation',  default: false },
  { key: 'parent_span_id',  label: 'Parent span',   default: false },
  { key: 'ended_at',        label: 'Ended',         default: false },

  // ── Fixed trailing ──
  { key: 'started_at',      label: 'Time',          default: true,  fixed: true },
];

export const FIXED_LEADING_KEYS = ['span_id', 'name'];
export const FIXED_TRAILING_KEYS = ['started_at'];
export const FIXED_SPAN_COLUMN_KEYS = [...FIXED_LEADING_KEYS, ...FIXED_TRAILING_KEYS];

export const CONFIGURABLE_SPAN_COLUMNS = ALL_SPAN_COLUMNS.filter((c) => !c.fixed);

export const DEFAULT_SPAN_COLUMN_KEYS = CONFIGURABLE_SPAN_COLUMNS
  .filter((c) => c.default)
  .map((c) => c.key);

/**
 * Resolve the full ordered list of column defs:
 *   [ ...fixed leading, ...enabled middle, ...fixed trailing ]
 * Fixed keys present in `enabledKeys` are dropped (added by position).
 */
export function resolveSpanColumns(enabledKeys) {
  const byKey = new Map(ALL_SPAN_COLUMNS.map((c) => [c.key, c]));
  const fixed = new Set(FIXED_SPAN_COLUMN_KEYS);
  const middle = (Array.isArray(enabledKeys) ? enabledKeys : [])
    .filter((k) => byKey.has(k) && !fixed.has(k))
    .map((k) => byKey.get(k));
  const lead = FIXED_LEADING_KEYS.map((k) => byKey.get(k)).filter(Boolean);
  const trail = FIXED_TRAILING_KEYS.map((k) => byKey.get(k)).filter(Boolean);
  return [...lead, ...middle, ...trail];
}

const STORAGE_PREFIX = 'agent-spans:columns:';

export function loadSpanColumnKeys(teamId) {
  if (!teamId) return [...DEFAULT_SPAN_COLUMN_KEYS];
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + teamId);
    if (!raw) return [...DEFAULT_SPAN_COLUMN_KEYS];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return [...DEFAULT_SPAN_COLUMN_KEYS];
    }
    const valid = new Set(CONFIGURABLE_SPAN_COLUMNS.map((c) => c.key));
    return parsed.filter((k) => valid.has(k));
  } catch {
    return [...DEFAULT_SPAN_COLUMN_KEYS];
  }
}

export function saveSpanColumnKeys(teamId, keys) {
  if (!teamId) return;
  try {
    localStorage.setItem(STORAGE_PREFIX + teamId, JSON.stringify(keys));
  } catch {
    /* ignore */
  }
}
